import { useState } from 'react';

interface SharePhotoButtonProps {
  photo: Blob;
  filename: string;
}

/** Lets the user get the original bill photo onto the real IRD form's file input — via the Share sheet where supported, a plain download otherwise. */
export default function SharePhotoButton({ photo, filename }: SharePhotoButtonProps) {
  const [error, setError] = useState<string | null>(null);

  async function handleShare() {
    setError(null);
    const file = new File([photo], filename, { type: photo.type || 'image/jpeg' });

    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'Bill photo' });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return; // user cancelled — not an error
      }
    }

    try {
      const url = URL.createObjectURL(photo);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setError("Couldn't prepare the photo for saving. Try taking a screenshot of the preview instead.");
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void handleShare()}
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 active:bg-slate-100"
      >
        📎 Share / Save Bill Photo
      </button>
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
